// Caesar Cipher
export const caesarEncode = (text, shift) => {
  return text
    .split('')
    .map(char => {
      if (char.match(/[a-z]/i)) {
        const code = char.charCodeAt(0);
        const isUpperCase = code >= 65 && code <= 90;
        const base = isUpperCase ? 65 : 97;
        return String.fromCharCode(((code - base + shift) % 26) + base);
      }
      return char;
    })
    .join('');
};

// Atbash Cipher
export const atbashEncode = (text) => {
  return text
    .split('')
    .map(char => {
      if (char.match(/[a-z]/i)) {
        const code = char.charCodeAt(0);
        const isUpperCase = code >= 65 && code <= 90;
        const base = isUpperCase ? 65 : 97;
        const offset = code - base;
        return String.fromCharCode(base + (25 - offset));
      }
      return char;
    })
    .join('');
};

// Aristocrat Cipher (Simple substitution)
export const aristocratEncode = (text, key) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const keyMap = {};
  
  // Create mapping from key
  for (let i = 0; i < alphabet.length; i++) {
    keyMap[alphabet[i]] = key[i];
  }

  return text
    .split('')
    .map(char => {
      const upperChar = char.toUpperCase();
      if (keyMap[upperChar]) {
        return char === upperChar ? keyMap[upperChar] : keyMap[upperChar].toLowerCase();
      }
      return char;
    })
    .join('');
};

// Generate a random substitution key for Aristocrat
export const generateAristocratKey = () => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const chars = alphabet.split('');
  
  // Fisher-Yates shuffle with derangement check
  for (let i = chars.length - 1; i > 0; i--) {
    let j;
    do {
      j = Math.floor(Math.random() * i);
    } while (j === i || chars[j] === alphabet[i]); // Ensure no letter maps to itself
    
    // Swap elements
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  
  // Final check to ensure no letter maps to itself
  for (let i = 0; i < chars.length; i++) {
    if (chars[i] === alphabet[i]) {
      // If we find a self-mapping, swap with a random other position
      const swapIndex = (i + 1 + Math.floor(Math.random() * (chars.length - 1))) % chars.length;
      [chars[i], chars[swapIndex]] = [chars[swapIndex], chars[i]];
    }
  }
  
  return chars.join('');
};

const API_URL = 'https://cipherrush-backend.onrender.com';

// Get a random quote from the database
export const getRandomQuote = async (difficulty = null) => {
  try {
    // Get the list of used quote IDs from sessionStorage
    const usedQuoteIds = JSON.parse(sessionStorage.getItem('usedQuoteIds') || '[]');
    
    const response = await fetch(`${API_URL}/quotes`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const quotes = await response.json();
    
    if (!quotes || quotes.length === 0) {
      throw new Error('No quotes found in the database');
    }

    let filteredQuotes = quotes;
    if (difficulty) {
      filteredQuotes = quotes.filter(quote => quote.difficulty === difficulty);
    }
    
    if (filteredQuotes.length === 0) {
      throw new Error(`No quotes found for difficulty: ${difficulty}`);
    }

    // Filter out quotes that have been used
    const availableQuotes = filteredQuotes.filter(quote => !usedQuoteIds.includes(quote.id));
    
    // If all quotes have been used, reset the used quotes list
    if (availableQuotes.length === 0) {
      sessionStorage.setItem('usedQuoteIds', '[]');
      return getRandomQuote(difficulty);
    }

    // Get a random quote from available quotes
    const randomIndex = Math.floor(Math.random() * availableQuotes.length);
    const selectedQuote = availableQuotes[randomIndex];

    // Add the selected quote ID to used quotes
    usedQuoteIds.push(selectedQuote.id);
    sessionStorage.setItem('usedQuoteIds', JSON.stringify(usedQuoteIds));
    
    return selectedQuote;
  } catch (error) {
    console.error('Error fetching random quote:', error);
    // Instead of returning a fallback quote, throw the error to handle it in the component
    throw error;
  }
};

// Save user progress
export const saveProgress = async (data) => {
  try {
    const response = await fetch(`${API_URL}/userProgress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return await response.json();
  } catch (error) {
    console.error('Error saving progress:', error);
    return null;
  }
};

// Vigenère Cipher
export const vigenereEncode = (text, key) => {
  let result = '';
  key = key.toUpperCase().replace(/[^A-Z]/g, '');
  let keyIndex = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (/[a-zA-Z]/.test(char)) {
      const isUpper = char === char.toUpperCase();
      const base = isUpper ? 65 : 97;
      const textCode = char.toUpperCase().charCodeAt(0) - 65;
      const keyCode = key[keyIndex % key.length].charCodeAt(0) - 65;
      const encodedCode = (textCode + keyCode) % 26;
      result += String.fromCharCode(base + encodedCode);
      keyIndex++;
    } else {
      result += char;
    }
  }
  return result;
};

export const vigenereDecode = (text, key) => {
  let result = '';
  key = key.toUpperCase().replace(/[^A-Z]/g, '');
  let keyIndex = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (/[a-zA-Z]/.test(char)) {
      const isUpper = char === char.toUpperCase();
      const base = isUpper ? 65 : 97;
      const textCode = char.toUpperCase().charCodeAt(0) - 65;
      const keyCode = key[keyIndex % key.length].charCodeAt(0) - 65;
      const decodedCode = (textCode - keyCode + 26) % 26;
      result += String.fromCharCode(base + decodedCode);
      keyIndex++;
    } else {
      result += char;
    }
  }
  return result;
};

export const generateVigenereKey = (length = 5) => {
  let key = '';
  for (let i = 0; i < length; i++) {
    key += String.fromCharCode(65 + Math.floor(Math.random() * 26));
  }
  return key;
};

// Hill Cipher
const mod = (n, m) => ((n % m) + m) % m;

// Matrix multiplication modulo 26
const matrixMultiply = (matrix, vector) => {
  const result = [];
  for (let i = 0; i < matrix.length; i++) {
    let sum = 0;
    for (let j = 0; j < vector.length; j++) {
      sum += matrix[i][j] * vector[j];
    }
    result.push(mod(sum, 26));
  }
  return result;
};

// Calculate matrix determinant
const determinant = (matrix) => {
  if (matrix.length === 2) {
    return mod(matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0], 26);
  }
  return mod(matrix[0][0] * (matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]) -
             matrix[0][1] * (matrix[1][0] * matrix[2][2] - matrix[1][2] * matrix[2][0]) +
             matrix[0][2] * (matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0]), 26);
};

// Calculate modular multiplicative inverse
const modInverse = (a, m) => {
  a = mod(a, m);
  for (let x = 1; x < m; x++) {
    if (mod(a * x, m) === 1) {
      return x;
    }
  }
  return null;
};

// Calculate matrix inverse modulo 26
const matrixInverse = (matrix) => {
  const det = determinant(matrix);
  const detInv = modInverse(det, 26);
  
  if (!detInv) {
    throw new Error('Matrix is not invertible modulo 26');
  }

  if (matrix.length === 2) {
    return [
      [mod(matrix[1][1] * detInv, 26), mod(-matrix[0][1] * detInv, 26)],
      [mod(-matrix[1][0] * detInv, 26), mod(matrix[0][0] * detInv, 26)]
    ];
  }

  // For 3x3 matrix
  const cofactors = [];
  for (let i = 0; i < 3; i++) {
    cofactors[i] = [];
    for (let j = 0; j < 3; j++) {
      const minor = [];
      for (let k = 0; k < 3; k++) {
        if (k !== i) {
          const row = [];
          for (let l = 0; l < 3; l++) {
            if (l !== j) {
              row.push(matrix[k][l]);
            }
          }
          minor.push(row);
        }
      }
      const cofactor = mod(Math.pow(-1, i + j) * determinant(minor) * detInv, 26);
      cofactors[i].push(cofactor);
    }
  }
  return cofactors;
};

// Generate a random invertible matrix
export const generateHillKey = (size = 2) => {
  let matrix;
  let det;
  do {
    matrix = Array(size).fill().map(() => 
      Array(size).fill().map(() => Math.floor(Math.random() * 26))
    );
    det = determinant(matrix);
  } while (!modInverse(det, 26));

  return matrix;
};

// Encode text using Hill cipher
export const hillEncode = (text, key) => {
  // Remove all non-alphabetic characters and convert to uppercase
  const cleanText = text.replace(/[^a-zA-Z]/g, '').toUpperCase();
  const size = key.length;
  const result = [];
  let currentBlock = [];

  // Process the text character by character
  for (let i = 0; i < cleanText.length; i++) {
    const num = cleanText.charCodeAt(i) - 65;
    currentBlock.push(num);
    
    if (currentBlock.length === size) {
      const encoded = matrixMultiply(key, currentBlock);
      result.push(...encoded);
      currentBlock = [];
    }
  }

  // Pad the last block if necessary
  if (currentBlock.length > 0) {
    while (currentBlock.length < size) {
      currentBlock.push(23); // Pad with 'X'
    }
    const encoded = matrixMultiply(key, currentBlock);
    result.push(...encoded);
  }

  // Convert numbers to letters
  return result.map(num => String.fromCharCode(num + 65)).join('');
};

// Decode text using Hill cipher
export const hillDecode = (text, key) => {
  // Remove all non-alphabetic characters and convert to uppercase
  const cleanText = text.replace(/[^a-zA-Z]/g, '').toUpperCase();
  const size = key.length;
  const result = [];
  let currentBlock = [];

  // Calculate inverse key
  const inverseKey = matrixInverse(key);

  // Process the text character by character
  for (let i = 0; i < cleanText.length; i++) {
    const num = cleanText.charCodeAt(i) - 65;
    currentBlock.push(num);
    
    if (currentBlock.length === size) {
      const decoded = matrixMultiply(inverseKey, currentBlock);
      // Check if this is the last block and contains padding
      const isLastBlock = i >= cleanText.length - size;
      if (isLastBlock) {
        // Look for 'X' padding (value 23) from the end
        let paddingCount = 0;
        for (let j = decoded.length - 1; j >= 0; j--) {
          if (decoded[j] === 23) {
            paddingCount++;
          } else {
            break;
          }
        }
        // Only add non-padding characters
        result.push(...decoded.slice(0, decoded.length - paddingCount));
      } else {
        result.push(...decoded);
      }
      currentBlock = [];
    }
  }

  // Convert numbers to letters
  return result.map(num => String.fromCharCode(num + 65)).join('');
};

// Get a random quote from the short quotes database
export const getRandomShortQuote = async () => {
  try {
    const response = await fetch(`${API_URL}/quotes`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const quotes = await response.json();
    
    if (!quotes || quotes.length === 0) {
      throw new Error('No quotes found in the database');
    }

    // Filter for quotes with less than 8 words
    const shortQuotes = quotes.filter(quote => quote.text.split(' ').length < 8);
    
    if (shortQuotes.length === 0) {
      throw new Error('No short quotes found in the database');
    }

    // Get a random quote from short quotes
    const randomIndex = Math.floor(Math.random() * shortQuotes.length);
    return shortQuotes[randomIndex];
  } catch (error) {
    console.error('Error fetching short quote:', error);
    throw error;
  }
}; 